import { useState, useEffect, useRef, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  X, ChevronRight, ChevronLeft, MapPin,
  Recycle, Droplets, Leaf, School, Coffee, Flag, Users, Building2, HeartPulse,
  Sprout, GraduationCap, Utensils, Landmark, Heart,
} from 'lucide-react';
import { patchProfile } from '../api/api';
import { MODAL_BG } from '../assets/modalBg';

const BLURU_BOUNDS = { north: 13.16, south: 12.79, east: 77.78, west: 77.41 };
const STEP_COUNT = 4;

const ICON_MAP = {
  Recycle, Droplets, Leaf, School, Coffee, Flag, Users, Building2, HeartPulse,
  Sprout, GraduationCap, Utensils, Landmark, Heart,
};

function loadGoogleMaps(apiKey, cb) {
  if (window.google?.maps?.places?.AutocompleteSuggestion) { cb(); return; }
  const existing = document.getElementById('gmap-script');
  if (existing) { existing.addEventListener('load', cb); return; }
  const s = document.createElement('script');
  s.id = 'gmap-script';
  s.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&v=weekly`;
  s.async = true;
  s.onload = cb;
  document.head.appendChild(s);
}

export default function OnboardingModal({ isOpen, onClose, user }) {
  const [step, setStep]             = useState(0);
  const [name, setName]             = useState('');
  const [locality, setLocality]     = useState('');
  const [localityLat, setLat]       = useState(null);
  const [localityLng, setLng]       = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [showSugg, setShowSugg]     = useState(false);
  const [contact, setContact]       = useState('');
  const [contactErr, setContactErr] = useState('');
  const [interests, setInterests]   = useState([]);
  const [saving, setSaving]         = useState(false);
  const [mapsReady, setMapsReady]   = useState(false);
  // 0=nothing  1=line1  2=line2  3=line3  4=content visible
  const [introPhase, setIntroPhase] = useState(0);
  const suggBoxRef = useRef(null);

  const askPhone = user?.auth_provider !== 'phone';

  // Lock body scroll while open
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev || ''; };
  }, [isOpen]);

  // Reset state on open
  useEffect(() => {
    if (isOpen) {
      setName(user?.name || '');
      setStep(0);
      setLocality(''); setLat(null); setLng(null);
      setContact(''); setContactErr('');
      setInterests(user?.interests || []);
      setSuggestions([]); setShowSugg(false);
    }
  }, [isOpen, user?.name, user?.auth_provider]); // eslint-disable-line react-hooks/exhaustive-deps

  // Intro animation — 3-second sequence
  useEffect(() => {
    if (!isOpen) return;
    setIntroPhase(0);
    const t1 = setTimeout(() => setIntroPhase(1), 80);
    const t2 = setTimeout(() => setIntroPhase(2), 1000);
    const t3 = setTimeout(() => setIntroPhase(3), 2000);
    const t4 = setTimeout(() => setIntroPhase(4), 3000);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); clearTimeout(t4); };
  }, [isOpen]);

  // Load Google Maps on locality step
  useEffect(() => {
    if (step !== 1) return;
    const key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    if (!key) return;
    loadGoogleMaps(key, () => setMapsReady(true));
    if (window.google?.maps?.places?.AutocompleteSuggestion) setMapsReady(true);
  }, [step]);

  // Close suggestions on outside click
  useEffect(() => {
    const handler = (e) => {
      if (suggBoxRef.current && !suggBoxRef.current.contains(e.target)) setShowSugg(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const { data: allCategories = [] } = useQuery({
    queryKey: ['categories'],
    queryFn: () => fetch(`${process.env.REACT_APP_BACKEND_URL}/api/categories`).then(r => r.json()),
    staleTime: Infinity,
  });
  const topCategories = allCategories.filter(c => !c.parent_id);

  const fetchSuggestions = useCallback(async (val) => {
    if (!val || val.length < 2 || !window.google?.maps?.places?.AutocompleteSuggestion) {
      setSuggestions([]); return;
    }
    try {
      const { suggestions: s } = await window.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input: val,
        locationRestriction: BLURU_BOUNDS,
        includedRegionCodes: ['in'],
      });
      setSuggestions(s.map(x => ({ text: x.placePrediction.text.toString(), prediction: x.placePrediction })));
      setShowSugg(true);
    } catch { setSuggestions([]); }
  }, []);

  const pickSuggestion = async (item) => {
    setLocality(item.text);
    setSuggestions([]); setShowSugg(false);
    try {
      const place = item.prediction.toPlace();
      await place.fetchFields({ fields: ['location', 'formattedAddress'] });
      if (place.location) { setLat(place.location.lat()); setLng(place.location.lng()); }
      if (place.formattedAddress) setLocality(place.formattedAddress);
    } catch { /* keep text-only locality */ }
  };

  const validateContact = (val) => {
    if (!val.trim()) return '';
    if (askPhone) {
      if (val.replace(/\D/g, '').length !== 10) return 'Enter 10-digit mobile number';
    } else {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val.trim())) return 'Enter a valid email address';
    }
    return '';
  };

  const saveStep = async () => {
    const payload = {};
    if (step === 0) {
      if (name.trim().length < 2) return false;
      payload.name = name.trim();
    }
    if (step === 1 && locality) {
      payload.location = locality;
      if (localityLat !== null) payload.location_lat = localityLat;
      if (localityLng !== null) payload.location_lng = localityLng;
    }
    if (step === 2 && contact.trim()) {
      const err = validateContact(contact);
      if (err) { setContactErr(err); return false; }
      if (askPhone) payload.phone = '+91' + contact.replace(/\D/g, '');
      else payload.email = contact.trim().toLowerCase();
    }
    if (Object.keys(payload).length > 0) {
      try {
        setSaving(true);
        await patchProfile(payload);
      } catch (e) {
        const msg = e?.response?.data?.detail;
        if (step === 2 && msg?.includes('already linked')) {
          setContactErr(msg); setSaving(false); return false;
        }
      } finally { setSaving(false); }
    }
    return true;
  };

  const handleNext = async () => {
    const ok = await saveStep();
    if (ok) setStep(s => Math.min(s + 1, STEP_COUNT - 1));
  };

  const handlePrev = () => { if (step > 0) setStep(s => s - 1); };

  const handleSubmit = async () => {
    setSaving(true);
    try { await patchProfile({ interests, onboarding_done: true }); }
    catch { /* best-effort */ } finally { setSaving(false); }
    onClose(true);
  };

  const handleClose = async () => {
    const payload = { onboarding_done: true };
    if (name.trim().length >= 2) payload.name = name.trim();
    if (locality) {
      payload.location = locality;
      if (localityLat !== null) payload.location_lat = localityLat;
      if (localityLng !== null) payload.location_lng = localityLng;
    }
    if (contact.trim() && !validateContact(contact)) {
      if (askPhone) payload.phone = '+91' + contact.replace(/\D/g, '');
      else payload.email = contact.trim().toLowerCase();
    }
    if (interests.length > 0) payload.interests = interests;
    try { await patchProfile(payload); } catch { /* best-effort */ }
    onClose(false);
  };

  const toggleInterest = (id) => {
    setInterests(prev => {
      if (prev.includes(id)) return prev.filter(x => x !== id);
      if (prev.length >= 5) return prev;
      return [...prev, id];
    });
  };

  const nextDisabled = saving || (step === 0 && name.trim().length < 2);

  if (!isOpen) return null;

  // Animation helpers
  const lineStyle = (phase) => ({
    opacity: introPhase >= phase ? 1 : 0,
    transform: introPhase >= phase ? 'translateY(0)' : 'translateY(10px)',
    transition: 'opacity 0.55s ease, transform 0.55s ease',
  });

  const contentStyle = {
    opacity: introPhase >= 4 ? 1 : 0,
    transform: introPhase >= 4 ? 'translateY(0)' : 'translateY(8px)',
    transition: 'opacity 0.45s ease, transform 0.45s ease',
    pointerEvents: introPhase >= 4 ? 'auto' : 'none',
  };

  // Shared label component
  const StepLabel = ({ title, sub }) => (
    <div className="mb-3">
      <p className="text-xs font-bold text-gray-800 uppercase tracking-wider">{title}</p>
      <p className="text-xs text-gray-500 mt-0.5">{sub}</p>
    </div>
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.65)' }}
    >
      <div
        data-testid="onboarding-modal"
        className="relative w-full max-w-md rounded-2xl shadow-2xl flex flex-col overflow-hidden"
        style={{
          minHeight: 560,
          maxHeight: '92vh',
          backgroundColor: '#f7f5f0',
          backgroundImage: `url(${MODAL_BG})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          data-testid="onboarding-close-btn"
          onClick={handleClose}
          className="absolute top-4 right-4 text-gray-300 hover:text-gray-500 transition-colors z-10"
          aria-label="Close"
        >
          <X size={20} />
        </button>

        {/* ── Animated intro header ── */}
        <div className="px-7 pt-8 pb-5 text-center space-y-2.5 flex-shrink-0">
          <div style={lineStyle(1)}>
            <h2 className="text-2xl font-bold text-gray-900 leading-snug">
              Welcome to Karmana!!!
            </h2>
          </div>
          <div style={lineStyle(2)}>
            <p className="text-[14px] font-semibold text-gray-800 leading-relaxed">
              You&apos;re now part of a community working to make Bengaluru a better place.
              You&apos;ve taken the first step.
            </p>
          </div>
          <div style={lineStyle(3)}>
            <p className="text-[14px] font-medium text-gray-700 leading-relaxed">
              Now, tell us a little about yourself so we can show you activities and causes that matter to you.
            </p>
          </div>
        </div>

        {/* ── Divider between intro and step content ── */}
        <div className="mx-7 border-t border-gray-200/80" style={contentStyle} />

        {/* ── Step content + nav buttons (flex-1) ── */}
        <div
          className="flex-1 px-7 pt-5 pb-2 flex flex-col min-h-0"
          style={contentStyle}
        >
          {/* ── Carousel track — slides left/right on Next/Prev ── */}
          <div className="flex-1 min-h-0 -mx-7 overflow-hidden">
            <div
              style={{
                display: 'flex',
                height: '100%',
                width: `${STEP_COUNT * 100}%`,
                transform: `translateX(-${(step * 100) / STEP_COUNT}%)`,
                transition: 'transform 0.32s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              {/* Step 0: Name */}
              <div style={{ width: `${100 / STEP_COUNT}%`, flexShrink: 0 }} className="px-7 flex flex-col">
                <StepLabel title="Your Name" sub="What should we call you" />
                <input
                  data-testid="onboarding-name-input"
                  autoFocus={introPhase >= 4 && step === 0}
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="Name"
                  maxLength={60}
                  className="w-full bg-white/70 border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent placeholder-gray-400"
                />
              </div>

              {/* Step 1: Locality */}
              <div style={{ width: `${100 / STEP_COUNT}%`, flexShrink: 0 }} className="px-7 flex flex-col">
                <StepLabel title="Your Locality" sub="So we show you nearby events" />
                <div className="relative" ref={suggBoxRef}>
                  <div className="flex items-center bg-white/70 border border-gray-200 rounded-xl px-4 py-3 focus-within:ring-2 focus-within:ring-emerald-400">
                    <MapPin size={15} className="text-gray-400 mr-2 flex-shrink-0" />
                    <input
                      data-testid="onboarding-locality-input"
                      autoFocus={introPhase >= 4 && step === 1}
                      type="text"
                      value={locality}
                      onChange={e => { setLocality(e.target.value); setLat(null); setLng(null); fetchSuggestions(e.target.value); }}
                      placeholder={mapsReady ? 'Start typing your area in Bengaluru…' : 'Loading…'}
                      className="flex-1 text-sm outline-none bg-transparent placeholder-gray-400"
                    />
                    {locality && (
                      <button onClick={() => { setLocality(''); setLat(null); setLng(null); setSuggestions([]); }} className="ml-1 text-gray-300 hover:text-gray-500">
                        <X size={13} />
                      </button>
                    )}
                  </div>
                  {showSugg && suggestions.length > 0 && (
                    <ul
                      data-testid="onboarding-locality-suggestions"
                      className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-100 rounded-xl shadow-lg z-20 max-h-36 overflow-y-auto"
                    >
                      {suggestions.map((s, i) => (
                        <li
                          key={i}
                          data-testid={`onboarding-locality-suggestion-${i}`}
                          onMouseDown={() => pickSuggestion(s)}
                          className="px-4 py-2.5 text-sm text-gray-700 hover:bg-emerald-50 cursor-pointer first:rounded-t-xl last:rounded-b-xl"
                        >
                          {s.text}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              {/* Step 2: Contact */}
              <div style={{ width: `${100 / STEP_COUNT}%`, flexShrink: 0 }} className="px-7 flex flex-col">
                <StepLabel
                  title={askPhone ? 'Your Phone Number' : 'Your Email ID'}
                  sub="Help us keep you updated"
                />
                {askPhone ? (
                  <div className="flex items-center bg-white/70 border border-gray-200 rounded-xl overflow-hidden focus-within:ring-2 focus-within:ring-emerald-400">
                    <span className="px-3 py-3 bg-gray-100/80 text-sm text-gray-600 border-r border-gray-200 select-none">+91</span>
                    <input
                      data-testid="onboarding-phone-input"
                      autoFocus={introPhase >= 4 && step === 2}
                      type="tel"
                      inputMode="numeric"
                      maxLength={10}
                      value={contact}
                      onChange={e => { const v = e.target.value.replace(/\D/g, '').slice(0, 10); setContact(v); setContactErr(''); }}
                      placeholder="10-digit mobile number"
                      className="flex-1 px-4 py-3 text-sm outline-none bg-transparent placeholder-gray-400"
                    />
                  </div>
                ) : (
                  <input
                    data-testid="onboarding-email-input"
                    autoFocus={introPhase >= 4 && step === 2}
                    type="email"
                    value={contact}
                    onChange={e => { setContact(e.target.value); setContactErr(''); }}
                    placeholder="Email address"
                    className="w-full bg-white/70 border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent placeholder-gray-400"
                  />
                )}
                {contactErr && <p className="text-xs text-red-500 mt-1.5">{contactErr}</p>}
              </div>

              {/* Step 3: Interests */}
              <div style={{ width: `${100 / STEP_COUNT}%`, flexShrink: 0 }} className="px-7 flex flex-col h-full">
                <div className="mb-3">
                  <p className="text-xs font-bold text-gray-800 uppercase tracking-wider">What are you Interested in?</p>
                  <p className={`text-xs mt-0.5 ${interests.length >= 5 ? 'text-amber-600 font-medium' : 'text-gray-500'}`}>
                    Pick up to 5 &nbsp;({interests.length}/5)
                  </p>
                </div>
                <div className="flex-1 overflow-y-auto min-h-0 pr-0.5">
                  <div className="flex flex-wrap gap-2 pb-2">
                    {topCategories.map(cat => {
                      const selected = interests.includes(cat.id);
                      const maxed = interests.length >= 5 && !selected;
                      const IconComp = ICON_MAP[cat.icon] || null;
                      return (
                        <button
                          key={cat.id}
                          data-testid={`onboarding-interest-${cat.id}`}
                          onClick={() => !maxed && toggleInterest(cat.id)}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border transition-all ${
                            selected
                              ? 'bg-emerald-500 border-emerald-500 text-white'
                              : maxed
                              ? 'bg-white/40 border-gray-200 text-gray-300 cursor-not-allowed'
                              : 'bg-white/70 border-gray-200 text-gray-700 hover:border-emerald-400 hover:text-emerald-600'
                          }`}
                        >
                          {IconComp && <IconComp size={13} className="flex-shrink-0" />}
                          {cat.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ── Nav buttons — immediately below carousel ── */}
          <div className="flex items-center gap-3 mt-4 flex-shrink-0">
            {/* Prev — always visible; disabled & muted on step 0 */}
            <button
              data-testid="onboarding-prev-btn"
              onClick={handlePrev}
              disabled={saving || step === 0}
              className={`flex items-center justify-center gap-1 flex-1 py-3 rounded-xl border text-sm font-medium transition-colors ${
                step === 0
                  ? 'border-gray-200/60 text-gray-300 bg-white/20 cursor-not-allowed'
                  : 'border-gray-300/80 text-gray-600 bg-white/30 hover:bg-white/50'
              }`}
            >
              <ChevronLeft size={15} />
              Prev
            </button>

            {step < STEP_COUNT - 1 ? (
              <button
                data-testid="onboarding-next-btn"
                onClick={handleNext}
                disabled={nextDisabled}
                className={`flex items-center justify-center gap-1 flex-1 py-3 rounded-xl text-sm font-semibold transition-all ${
                  nextDisabled
                    ? 'bg-gray-200/60 text-gray-400 cursor-not-allowed'
                    : 'bg-emerald-600 text-white hover:bg-emerald-700 shadow-sm'
                }`}
              >
                {saving ? 'Saving…' : 'Next'}
                {!saving && <ChevronRight size={15} />}
              </button>
            ) : (
              <button
                data-testid="onboarding-submit-btn"
                onClick={handleSubmit}
                disabled={saving}
                className="flex items-center justify-center flex-1 py-3 rounded-xl text-sm font-semibold bg-emerald-600 text-white hover:bg-emerald-700 shadow-sm transition-all disabled:opacity-50"
              >
                {saving ? 'Saving…' : 'Done'}
              </button>
            )}
          </div>
        </div>

        {/* ── Step dots — sliding pill carousel indicator ── */}
        <div className="flex justify-center py-3 flex-shrink-0" style={contentStyle} data-testid="onboarding-step-dots">
          <div className="relative flex gap-2 items-center">
            {/* Background dots — all same size */}
            {Array.from({ length: STEP_COUNT }).map((_, i) => (
              <span
                key={i}
                data-testid={`onboarding-step-dot-${i}`}
                className="h-2 w-2 rounded-full bg-gray-300/70 flex-shrink-0"
              />
            ))}
            {/* Sliding active pill — glides between dot positions */}
            <span
              className="absolute top-0 h-2 w-6 rounded-full bg-emerald-500 pointer-events-none"
              style={{
                left: '50%',
                transform: `translateX(calc(-50% + ${(step - (STEP_COUNT - 1) / 2) * 16}px))`,
                transition: 'transform 0.32s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            />
          </div>
        </div>

        {/* ── Karmana logo block (bottom center) ── */}
        <div className="flex items-center justify-center gap-2 pb-5 pt-2 border-t border-gray-200/60 mx-7 flex-shrink-0">
          <img
            src="/images/logo.webp"
            alt="Karmana"
            className="w-8 h-8 rounded-full object-contain"
          />
          <div className="text-left">
            <p className="text-sm font-bold text-gray-900 leading-tight">Karmana</p>
            <p className="text-[10px] text-gray-500 leading-tight">Bengaluru&apos;s Civic Action Platform</p>
          </div>
        </div>
      </div>
    </div>
  );
}
